import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { LicenseGeneralFeature, LicenseProductType, LicenseService, LicenseTestFeature, LocalStorage } from '@isophi/core-legacy';
import { IndividualChildResultPreview, Product } from '@isophi/teachers-shared';
import { combineLatest, Observable, of, Subject, takeUntil } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ChildService } from '../../../../core/services/child.service';
import { KindergartenService } from '../../../../core/services/kindergarten.service';
import { PopupService } from '../../../../core/services/popup.service';
import { SwitchToDemoKindergartenService } from '../../../../core/services/switch-to-demo-kindergarten.service';
import { DashboardService, ResultType } from '../../services/dashboard.service';

@Component({
    selector: 'app-index',
    templateUrl: './index.component.html',
    styleUrls: ['./index.component.scss'],
    standalone: false
})
export class IndexComponent implements OnInit, OnDestroy {
  static readonly IS_EXPERIENCED_KINDERGARTEN_KEY = 'isExperiencedKindergarten';

  readonly switchDemoKindergartenService = inject(SwitchToDemoKindergartenService);

  private readonly kindergartenService = inject(KindergartenService);

  private readonly innitialPopupService = inject(PopupService);

  private readonly ngUnsubscribe$ = new Subject<void>();

  private hasShowcaseFeature = false;

  licenses$: Observable<Record<string, boolean>>;

  isExperiencedKindergarten$: Observable<boolean> = of(false);

  childResultsPreviewCompleted$: Observable<IndividualChildResultPreview[]>;

  childResultsPreviewUnCompleted$: Observable<IndividualChildResultPreview[]>;

  ownedProducts$: Observable<Product[]>;

  recommendedProducts$: Observable<Product[]>;

  private storage: LocalStorage;

  protected isSwitchKindergartenVisible = true;

  protected showPromo$: Observable<boolean>;

  constructor(private licenseService: LicenseService, protected childService: ChildService, protected dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.storage = new LocalStorage();
    this.initLicenses();
    this.isExperiencedKindergarten$ = this.isExperiencedKindergarten();
    this.childResultsPreviewCompleted$ = this.dashboardService.getLatestChildResultsPreview(ResultType.COMPLETED);
    this.childResultsPreviewUnCompleted$ = this.dashboardService.getLatestChildResultsPreview(ResultType.UNCOMPLETED);
    this.ownedProducts$ = this.dashboardService.getOwnedProducts();
    this.recommendedProducts$ = this.dashboardService.getRecommendedProducts();
    this.innitialPopupService.tryShowPopup('t_app_initial');

    this.licenseService.canSeeDemoKindergarten.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((hasShowcase) => {
      this.hasShowcaseFeature = hasShowcase;
    });

    this.showPromo$ = this.licenses$.pipe(
      map((licences) => {
        return licences.showProducts && !this.kindergartenService.loggedKindergarten.isSK();
      }),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  }

  /**
   * Checks conditions concerning "experience" of kindergarten with diagnostic tools.
   * (Such as number of completed diagnostics)
   *
   * @returns isExperiencedKindergarten: Observable<boolean>
   */
  isExperiencedKindergarten(): Observable<boolean> {
    const storageVal = this.storage.getItem(IndexComponent.IS_EXPERIENCED_KINDERGARTEN_KEY);
    let isExperiencedKindergarten = JSON.parse(storageVal) === true;

    // Return Observable of true, if no further checks are needed
    if (isExperiencedKindergarten) return of(true);

    // Else ... Check number of completed diagnostics to determine experience level
    return this.childService.getChildren().pipe(
      map((children) => {
        let numberOfChildrenWithResults = 0;
        children.forEach((child) => {
          if (child.testResultsCount.length > 0) numberOfChildrenWithResults++;
        });
        isExperiencedKindergarten = numberOfChildrenWithResults > 2;

        this.storage.setItem(IndexComponent.IS_EXPERIENCED_KINDERGARTEN_KEY, isExperiencedKindergarten.toString(), true);

        return isExperiencedKindergarten;
      })
    );
  }

  /**
   * Init licenses for this component.
   * Based on them some sections are shown/hidden.
   */
  private initLicenses(): void {
    this.licenses$ = combineLatest({
      diagRecord: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST, LicenseTestFeature.TEST_RECORD),
      childrenManagement: this.licenseService.hasAnyProductWithFeature(
        LicenseProductType.GENERAL,
        LicenseGeneralFeature.CHILDREN_MANAGEMENT
      ),
      displayTestResult: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST, LicenseTestFeature.DISPLAY_TEST_RESULT),
      testSupport: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST, LicenseTestFeature.TEST_SUPPORT),
      diagRecordZ: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST_Z, LicenseTestFeature.TEST_RECORD),
      displayTestResultZ: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST_Z, LicenseTestFeature.DISPLAY_TEST_RESULT),
      testSupportZ: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST_Z, LicenseTestFeature.TEST_SUPPORT),
      showProducts: this.licenseService.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.SHOW_PRODUCTS),
      evalCalculator: this.licenseService.hasAnyProductWithFeature(LicenseProductType.TEST, LicenseTestFeature.EVALUATION_CALCULATOR),
      dashboardSK: this.licenseService.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.DASHBOARD_SK),
      dashboardSchool: this.licenseService.hasAnyProductWithFeature(LicenseProductType.GENERAL, LicenseGeneralFeature.DASHBOARD_SCHOOL),
      childPortfolio: this.licenseService.canSeeChildPortfolio,
      managementTrial: this.licenseService.hasTrialManagement,
      managementAccess: this.licenseService.accessManagement,
      testKit: this.licenseService.hasAnyTestKit,
    });
  }

  get isSwitchDemoKindergartenVisible(): boolean {
    return !this.kindergartenService.loggedKindergarten.isDemo && this.isSwitchKindergartenVisible && this.hasShowcaseFeature;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  get isDemoKindergarten(): boolean {
    return this.kindergartenService.loggedKindergarten.isDemo;
  }
}
